<template>
  <transition
    name="report-panel-transition"
    enter-active-class="animate__animated animate__slideInRight animate__faster"
    leave-active-class="animate__animated animate__slideOutRight animate__faster"
  >
    <div
      class="report-panel"
      :class="{'in-editor': $route.name === 'PresentationDetail'}"
      v-if="selectedPresentation && isShowReportPanel"
      v-click-outside="onClickOutSideReportPanel"
      :style="{
        'z-index': $route.name === 'PresentationDetail' ? '10' : '3'
      }"
    >
      <ReportHeader :presentation="selectedPresentation" />
      <div v-if="reportError || connectionError">
        <p class="text-base-70">
          {{ $i18nPresenter.t('Something went wrong when getting report for this presentation.') }}
          Please <a @click="reload()" class="cursor-pointer text-link--primary">reload</a> your browser.
        </p>
      </div>
      <div v-else>
        <transition
          name="report-panel-engagement-transition"
          enter-active-class="animate__animated animate__fadeIn animate__faster"
        >
          <div>
            <div class="report-engagement-rate">
              <ReportEngagementRate
                :engagementRate="engagementRate"
                :engagementLevel="engagementLevel"
              />
              <div class="mt-1.5 mb-4 border-b border-base-30 w-full"></div>
              <ReportTotalResponses :totalInteractions="totalInteractions" />
            </div>
          </div>
        </transition>

        <div class="nav-tabs mt-5">
          <div
            class="nav-item"
            :class="{ active: selectedTab === 'engagingSlides' }"
            v-tooltip="$i18nPresenter.t('Most engaging slides')"
            @click="handleTabChange('engagingSlides')"
          >
            <i class="fas fa-heart"></i>
          </div>
          <div
            class="nav-item"
            :class="{ active: selectedTab === 'feedback' }"
            v-tooltip="$i18nPresenter.t('Feedback')"
            @click="handleTabChange('feedback')"
          >
            <aha-icon
              name="feedback-filled"
              width="16"
              height="16"
              viewBox="0 0 21 20"
            />
          </div>
          <div
            class="nav-item"
            :class="{ active: selectedTab === 'correctAnswers' }"
            v-tooltip="$i18nPresenter.t('Correct answers')"
            @click="handleTabChange('correctAnswers')"
          >
            <i class="fas fa-check-circle"></i>
          </div>
          <div
            v-if="$route.name !== 'PresentationDetail'"
            class="nav-item"
            :class="{ active: selectedTab === 'export' }"
            v-tooltip="$i18nPresenter.t('Export')"
            @click="handleTabChange('export')"
          >
            <i class="fas fa-file-download"></i>
          </div>
        </div>

        <report-top-engaging-slides v-if="selectedTab === 'engagingSlides'" />

        <ReportPrivateFeedbackV2
          v-if="selectedTab === 'feedback'"
          :isLoadingReport="isLoadingReport"
          :oldFeedbacks="privateFeedbacks"
          :newFeedbacks="newPrivateFeedbacks"
        />

        <report-quizzes-and-questions v-if="selectedTab === 'correctAnswers'" />

        <report-export
          v-if="selectedTab === 'export'"
          @handleShowResultsClick="handleShowResultsClick"
        />
      </div>
    </div>
  </transition>
</template>

<script>
// components
import { mapActions, mapGetters, mapState } from 'vuex'
import Utils from '@/util'
import constants from '@/constant'
import ReportHeader from './ReportHeader.vue'
import ReportEngagementRate from './ReportEngagementRate.vue'
import ReportTotalResponses from './ReportTotalResponses.vue'
import ReportTopEngagingSlides from './ReportTopEngagingSlides.vue'
import ReportQuizzesAndQuestions from './ReportQuizzesAndQuestions.vue'
import ReportPrivateFeedbackV2 from './ReportPrivateFeedbackV2.vue'
import ReportExport from './ReportExport.vue'

export default {
  components: {
    ReportHeader,
    ReportEngagementRate,
    ReportTotalResponses,
    ReportTopEngagingSlides,
    ReportQuizzesAndQuestions,
    ReportExport,
    ReportPrivateFeedbackV2,
  },

  data() {
    return {
      selectedTab: 'engagingSlides',
    }
  },
  computed: {
    ...mapGetters('presentationReport', [
      'presentationReport',
      'selectedPresentation',
      'isLoadingReport',
      'reportError',
      'connectionError',
      'isShowReportPanel',
      'mostDifficultQuestions',
      'engagementRate',
      'engagementLevel',
      'totalInteractions',
      'privateFeedbacks',
      'newPrivateFeedbacks',
    ]),
    ...mapState({
      presentation: (state) => state.presentation,
    }),
  },
  mounted() {
    this.setDefaultTabInEditor()
  },
  methods: {
    ...mapActions('presentationReport', ['clearPresentationReportAction', 'unsubscribeReportAction']),
    ...mapActions([
      'patchPresentationAction',
    ]),
    handleShowResultsClick() {
      const { id } = this.selectedPresentation
      this.closeReport()
      Utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'Report > Click go to Results tab')
      window.setTimeout(() => {
        this.$router.push({
          name: 'PresentationDetail',
          params: { id },
          query: {
            export: true,
          },
        })
      }, 1000) // wait a bit to close report panel first
    },

    onClickOutSideReportPanel() {
      if (this.$route.name !== 'PresentationDetail') {
        this.closeReport()
      }
    },
    closeReport() {
      document.body.classList.remove('no-scroll')
      this.unsubscribeReportAction()
      this.clearPresentationReportAction()
      const eventCategory = this.$route.name === 'PresentationDetail' ?
        constants.eventCategories.PRESENTATION_EDITOR :
        constants.eventCategories.PRESENTATION_LIST
      Utils.mixpanelTracking(eventCategory, 'Report > Hide report', null, null, {
        presentationId: this.presentation.id,
      })
    },
    reload() {
      window.location.reload()
    },
    handleTabChange(tab) {
      this.selectedTab = tab
      const eventCategory = this.$route.name === 'PresentationDetail' ?
        constants.eventCategories.PRESENTATION_EDITOR :
        constants.eventCategories.PRESENTATION_LIST
      Utils.mixpanelTracking(eventCategory, 'Report > Switch tab', tab, null, {
        presentationId: this.presentation.id,
      })
    },
    setDefaultTabInEditor() {
      const hasOldFeedbacks = this.privateFeedbacks && this.privateFeedbacks.length

      const comments = this.newPrivateFeedbacks
        && this.newPrivateFeedbacks.comments
        && this.newPrivateFeedbacks.comments.length
      const ratings = this.newPrivateFeedbacks
        && this.newPrivateFeedbacks.ratings
        && this.newPrivateFeedbacks.ratings.length

      const hasNewFeedbacks = comments || ratings

      if (hasOldFeedbacks || hasNewFeedbacks) {
        this.selectedTab = 'feedback'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss-v2/variables.scss";

.report-panel {
  @apply bg-base-0 w-full p-4 pb-40 transition-all shadow-top bg-no-repeat;
  @apply right-0 overflow-y-scroll;
  @apply h-full fixed;
  @apply top-16;
  background-size: 100%;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* Hide scrollbar on IE and Edge */
  scrollbar-width: none;

  .report-panel-export {
    display: block;
  }

  &.in-editor {
    width: 100%;
    .report-panel-export {
      display: none;
    }
  }

  @media (min-width: 768px) { // sm screens
    min-height: calc(100vh - 4rem); // header height is 4rem
    width: 354px;
    &.in-editor {
      width: $rightSideWidth;
      @apply top-32;
    }
  }
}

.report-engagement-rate {
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  width: 100%;
  background: #FFFFFF;
  box-shadow: 0px 2px 6px rgba(211, 215, 225, 0.65);
  border-radius: 8px;
}

.nav-tabs {
  display: flex;
  margin-left: -1rem;
  margin-right: -1rem;
  border-top: 1px solid #EBEBEB;

  .nav-item {
    @apply bg-secondary-15 text-center text-secondary-40 cursor-pointer;
    @apply flex justify-center items-center flex-1;
    padding: 14px;

    &.active {
      @apply bg-base-0;
      color: #191F2D;
    }
  }
}
</style>
